import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

const MOBILE_QUERY = '(max-width: 760px)';

function isMobileViewport() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(MOBILE_QUERY).matches
  );
}

// A wrapping row of filter chips. On phones it shows only the first `rows`
// lines and offers a toggle for the rest; wider screens always show them all.
// The hidden chips stay laid out (just clipped), so measuring never needs a
// second render — they are made `inert` so keyboard focus skips them.
function ChipRows({ language, rows = 2, activeKey = '', className = '', ariaLabel, children }) {
  const [expanded, setExpanded] = useState(false);
  const [clip, setClip] = useState(null); // { height, full, hiddenCount } when collapsible
  const listRef = useRef(null);
  const listId = useId();

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    const chips = [...list.children];
    if (!isMobileViewport() || chips.length === 0) {
      setClip(null);
      return;
    }
    const listTop = list.getBoundingClientRect().top;
    const rowTops = [...new Set(chips.map((chip) => Math.round(chip.offsetTop)))].sort((a, b) => a - b);
    if (rowTops.length <= rows) {
      setClip(null);
      return;
    }
    const lastVisibleRowTop = rowTops[rows - 1];
    const visible = chips.filter((chip) => Math.round(chip.offsetTop) <= lastVisibleRowTop);
    const bottom = Math.max(...visible.map((chip) => chip.getBoundingClientRect().bottom - listTop));
    setClip({
      height: Math.ceil(bottom) + 2,
      full: list.scrollHeight,
      hiddenCount: chips.length - visible.length,
    });
  }, [rows]);

  useLayoutEffect(() => {
    measure();
  }, [measure, children, language]);

  useEffect(() => {
    const list = listRef.current;
    if (typeof window.ResizeObserver === 'function' && list) {
      const observer = new window.ResizeObserver(measure);
      observer.observe(list);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const collapsed = Boolean(clip) && !expanded;

  // Keep clipped chips out of the tab order while collapsed.
  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    [...list.children].forEach((chip) => {
      const hidden = collapsed && chip.offsetTop + 1 >= clip.height;
      chip.inert = hidden;
    });
  }, [collapsed, clip, children]);

  // A tag picked from a card may live in a clipped row: open up so the
  // active chip is visible.
  useEffect(() => {
    const list = listRef.current;
    if (!activeKey || !list || !clip) {
      return;
    }
    const active = list.querySelector('.is-active');
    if (active && active.offsetTop + 1 >= clip.height) {
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const label = expanded
    ? language === 'ja'
      ? 'タグをたたむ'
      : 'Fewer tags'
    : language === 'ja'
      ? `他のタグを見る（+${clip?.hiddenCount ?? 0}）`
      : `More tags (+${clip?.hiddenCount ?? 0})`;

  return (
    <div className={`chip-rows ${className}`}>
      <div
        id={listId}
        ref={listRef}
        role="group"
        aria-label={ariaLabel}
        className={`chip-rows__list ${collapsed ? 'is-collapsed' : ''}`}
        style={clip ? { maxHeight: `${collapsed ? clip.height : clip.full}px` } : undefined}
      >
        {children}
      </div>
      {clip ? (
        <button
          type="button"
          className="chip-rows__toggle"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {label}
          <span className="chip-rows__chevron" aria-hidden="true">
            {expanded ? '▴' : '▾'}
          </span>
        </button>
      ) : null}
    </div>
  );
}

export default ChipRows;
